import React, { useCallback, useContext, useState } from 'react'
import { Button, DatePicker, TravelerSelection } from '@pcln/horizon'
import type { ITravelerInfo } from '@pcln/horizon'
import type { DateRange } from 'react-day-picker'
import { format } from 'date-fns/format'
import { addDays } from 'date-fns'
import { useForm } from 'react-hook-form'
import pclnExperimentation from '@pcln/experimentation'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import type { GA4PageNameType } from '@/ga4types'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import { SearchFormContext } from '@/context/SearchFormContext'
import { yupResolver } from '@hookform/resolvers/yup'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import MultiHotelForm from './HorizonMultiHotelForm'
import {
  getFormContainerClass,
  getGridContainerClass,
  getInputClass
} from './HorizonHotelForm.variants'
import type {
  HOTEL_FORM_SUBMIT_TYPE,
  HOTEL_FORM_STATE_TYPE,
  SEM_SEARCH_TYPE
} from '../types'
import useHotelFormInitialValues from './useHotelFormInitialValues'
import useHotelFormSubmitValues from './useHotelFormSubmitValues'
import {
  INFANT_CAPACITY_LIMIT,
  isInfantLimitReachedPackages,
  searchFormSchema
} from '../SearchForm/validation'
import FormTypeRadios from './HorizonFormTypeRadio'
import { groupDealsURL } from '../SearchForm/utils'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  // promoDeal?: PromoDealType
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showMultiHotel?: boolean
  pageName?: 'homepage' | 'landing'
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showMultiHotel = false,
  pageName = 'homepage'
}: FormType) {
  const [hotelType, setHotelType] = useState('single')
  const [addFlights, setAddFlights] = useState(false)
  const { registerTabElement } = useTabGroup()
  const { updateFormValues } = useContext(SearchFormContext)
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH } =
    useHotelFormInitialValues()

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm<HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers
  } = useHotelFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    initialValues,
    setLocation,
    updateFormValues
  })

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    flights: false,
    car: false
  })

  const onCalendarChange = useCallback(
    (dates: DateRange | undefined): void => {
      if (dates?.from) {
        const startDate = format(dates.from, 'yyyyMMdd')
        setValue('startDate', startDate, { shouldDirty: true })
      }

      if (dates?.to) {
        const endDate = format(dates.to, 'yyyyMMdd')
        setValue('endDate', endDate, { shouldDirty: true })
      }
    },
    [setValue]
  )

  const hotelPlannerLink = groupDealsURL({
    location: watchEndLocation ? watchEndLocation.itemName : '',
    startDate: watchStartDate,
    endDate: watchEndDate
  })
  const hotelPlannerLinkAttrsProp = {
    href: hotelPlannerLink,
    target: '_blank'
  }

  const onItemSelect = useCallback(
    (
      item: LOCATION_SEARCH_TYPE,
      searchKey: 'startLocation' | 'endLocation'
    ) => {
      if (item) {
        setValue(searchKey, item)
      }
      // TODO: FIX AUTO TAB CONTROL AFTER VALUE SELECTED
      // if (!isMobile && !!item && focusNextElement) {
      //   focusNextElement()
      // } else
      if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue]
  )

  const handleSubmitHelper = handleSubmit((data: HOTEL_FORM_STATE_TYPE) => {
    let userLatitude: number | undefined
    let userLongitude: number | undefined
    if (
      data.endLocation?.type === 'GEO' &&
      data.endLocation?.lat &&
      data.endLocation?.lon
    ) {
      userLatitude = data.endLocation.lat
      userLongitude = data.endLocation.lon
    }
    const formValues = {
      ...data,
      ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
      ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
      mapView: false,
      useCurrentLocation: data.endLocation?.type === 'GEO',
      ...(userLatitude &&
        userLongitude && {
          userLatitude,
          userLongitude
        })
    }
    onSubmit(formValues, pageName)
    logSearchFormAnalytics(cguid, pageName, 'hotels', isMobile)
  })

  const handleTravelerChange = (travelerInfo: ITravelerInfo): void => {
    setValue('travelers', travelerInfo as TravelerState)
  }

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections(prev => ({
        ...prev,
        [optionValue]: checked
      }))
      setValue('tripType', updatedTripType)
      if (optionValue === 'FLY') {
        setAddFlights(checked)
      }
    },
    [setValue]
  )

  const formClasses = getFormContainerClass(hotelType, addFlights)
  const gridContainerClass = getGridContainerClass(hotelType, addFlights)

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-search-form"
        onSubmit={handleSubmitHelper}
      >
        <div className={formClasses}>
          {hotelType === 'multi' && showMultiHotel ? (
            <MultiHotelForm
              radioValue={hotelType}
              setRadioValue={setHotelType}
            />
          ) : (
            <div className={gridContainerClass}>
              <div className={getInputClass('radio', hotelType, addFlights)}>
                <FormTypeRadios formType={hotelType} onChange={setHotelType} />
              </div>

              <div className={getInputClass('location', hotelType, addFlights)}>
                <AutocompleteWrapper
                  errorMessage={
                    addFlights
                      ? errors.startLocation?.message
                      : errors.endLocation?.message
                  }
                  searchKey={addFlights ? 'startLocation' : 'endLocation'}
                  label={addFlights ? 'Where from?' : 'Where to?'}
                  initialSelectedItem={
                    watchEndLocation as LOCATION_SEARCH_TYPE | undefined
                  }
                  typeAheadEndpoint="hotels"
                  onItemSelect={(itemSelected: LOCATION_SEARCH_TYPE) => {
                    onItemSelect(itemSelected, 'endLocation')
                  }}
                  ref={(elem: HTMLInputElement) => {
                    registerTabElement(elem)
                  }}
                />
              </div>

              {addFlights && (
                <div className={getInputClass('flight', hotelType, addFlights)}>
                  <AutocompleteWrapper
                    errorMessage={errors.endLocation?.message}
                    searchKey="endLocation"
                    label="Where to?"
                    typeAheadEndpoint="flights"
                    initialSelectedItem={
                      watchStartLocation as LOCATION_SEARCH_TYPE | undefined
                    }
                    onItemSelect={(itemSelected: LOCATION_SEARCH_TYPE) => {
                      onItemSelect(itemSelected, 'startLocation')
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                  />
                </div>
              )}

              <div
                className={getInputClass('datePicker', hotelType, addFlights)}
              >
                <DatePicker
                  isMobile={isMobile}
                  inputRef={registerTabElement}
                  errorMessage={(() => {
                    if (errors.startDate) {
                      return errors.startDate.message
                    }
                    if (errors.endDate) {
                      return errors.endDate.message
                    }
                  })()}
                  className="w-auto"
                  title="Hotels: Check-in - Check-out"
                  label="Check-in - Check-out"
                  mode="range"
                  numberOfMonths={2}
                  handleCalendarChange={onCalendarChange}
                  defaultMonth={new Date()}
                  selected={{
                    from: new Date(),
                    to: addDays(new Date(), 1)
                  }}
                />
              </div>
              <div
                className={getInputClass('travelers', hotelType, addFlights)}
              >
                <TravelerSelection
                  isMobile={isMobile}
                  isHotel
                  roomsEnabled
                  maxRooms={watchTripType === 'STAY' ? 9 : 4}
                  maxChildren={watchTripType === 'STAY' ? 8 : undefined}
                  maxTravelers={watchTripType === 'STAY' ? 36 : 8}
                  fireMaxRoomsOnClick={() => {
                    pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
                    window.open(hotelPlannerLinkAttrsProp.href, '_blank')
                    fireMaxRoomsEvent(pageName, 'hotel')
                  }}
                  ageSelectEnabled
                  tooltipErrorMessageId="travelerSelectionError"
                  errorMessage={
                    errors?.travelers?.children?.message ||
                    errors?.travelers?.rooms?.message ||
                    (isInfantLimitReachedPackages(
                      watchTravelers?.adults,
                      watchTravelers?.childrenAges,
                      watchTripType
                    ) &&
                      INFANT_CAPACITY_LIMIT) ||
                    ''
                  }
                  onChange={(values: ITravelerInfo) => {
                    handleTravelerChange(values)
                  }}
                />
              </div>
              <div
                className={getInputClass('packageDeals', hotelType, addFlights)}
              >
                <HorizonBundleAndSave
                  productType="hotels"
                  checkedValues={bundleSelections}
                  onSelectionChange={handleBundleSelectionChange}
                  tripType={watchTripType}
                />
              </div>
              <Button
                buttonType="submit"
                type="primary"
                size="lg"
                className={getInputClass('submit', hotelType, addFlights)}
              >
                Search
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
